/**
 * THE ONLY `import.meta.env` READ IN THE APP. `tests/structure.test.ts` (3) enforces it.
 *
 * Repeating this read per call site — it has been measured at six files plus one inline copy — lets a base
 * URL differ between two call sites in one bundle, and the way that surfaces is a screen that works on
 * staging and 404s in production, with nothing in the diff to point at.
 *
 * TWO BASES, because there are two servers. The READ surface is the fold process (`apps/api`, one public
 * port, prefix `/v1`). The TRADER surface is a separate process under `/user/v1` — a distinct TOP-LEVEL
 * segment so nginx is one block and a new trader route can never be silently mis-routed into the read app.
 * `TRADE_BASE` is what `api/http.ts` selects on `r.base === "trade"`, so every one of `api/trade.ts`'s
 * nineteen routes goes through this one constant.
 */

/** The read surface. Relative by default: the dev server proxies `/v1` to `:8091` and nginx serves the built
 *  app from the SAME ORIGIN, so a relative base is correct in both and configures nothing. */
export const MARKET_BASE: string = import.meta.env.VITE_MARKET_BASE ?? "/v1";

/**
 * WHERE A TOKEN'S LOGO IS LOADED FROM — OUR ORIGIN, NEVER THE UPSTREAM URL ON THE ROW.
 *
 * MEASURED 2026-08-19: `ipfs.io`, which serves 41% of every row on the launch board, began answering a
 * BROWSER-shaped request with `403` + `cf-mitigated: challenge` while still answering our server with
 * `200`. An `<img>` load has no JS context and can never solve that challenge, so those logos are
 * unreachable from this page and perfectly reachable from the API — which is why the fetch moved there.
 * The same route also answers Referer-keyed hotlink protection (`m.rapidlaunch.io`), a different mechanism
 * with an identical symptom.
 *
 * THE ROW STILL CARRIES `image`, AND IT IS STILL THE TRUTH — the upstream url the mint's own metadata
 * document names. It is the provenance behind the `uri`-clone mark, and it is how a client knows whether a
 * logo EXISTS at all: this function is only ever called when `image !== null`, so no new wire field was
 * needed for any of this.
 */
/**
 * ⚠️ `?v=` IS A CACHE BUST AND IT IS LOAD-BEARING, NOT COSMETIC.
 *
 * The relay answers real bytes with `Cache-Control: public, max-age=2592000, immutable`, and headers are
 * cached WITH the response. Between 2026-08-19 and 2026-09-11 those bytes also carried
 * `Content-Security-Policy: default-src 'none'; sandbox`, which makes an `<img>` refuse to paint a
 * perfectly valid 200 — so every browser that opened a token page in that window holds a THIRTY-DAY
 * immutable copy of a logo it cannot render, and fixing the server reaches none of them.
 *
 * Bumping this changes the URL the browser keys on, so it refetches once and the poisoned entries age out.
 * nginx is unaffected: its `proxy_cache_key` is `$uri`, which excludes the query string, so this costs no
 * extra upstream fetch and no second cached object.
 *
 * Bump it ONLY when a shipped response was wrong in a way the cache preserves — a wrong header, wrong
 * bytes. Never for a routine deploy.
 */
export const LOGO_CACHE_EPOCH = "2";

export function logoSrc(mint: string): string {
  return `${MARKET_BASE}/token/${encodeURIComponent(mint)}/logo?v=${LOGO_CACHE_EPOCH}`;
}

/** The trader surface. Same origin, DIFFERENT top-level segment, and nginx routes it to a different port
 *  (8092) than `/v1` (8091) — see `deploy/nginx/kinetiq.conf`. A **502 from this base means the server-side
 *  surface is not switched on**, not that the client is misconfigured: it binds only when `USER_API_ENABLED`
 *  is set (`apps/api/api-security.md` §6.1). */
export const TRADE_BASE: string = import.meta.env.VITE_TRADE_BASE ?? "/user/v1";

/**
 * THE SIGNING VENDOR'S PARENT ORGANISATION ID.
 *
 * It is a PUBLIC identifier, not a secret: it names the organisation a user's sub-organisation is created
 * under, and it is visible in every request the SDK makes from the browser anyway. It is build-time config
 * rather than a server read because the trader surface serves no `/auth/config` route — and inventing a
 * client-side default for it would produce an SDK that initialises against the wrong organisation and fails
 * at the signature with a vendor error nobody can map back to here.
 *
 * EMPTY means signing is not configured in this build, and the UI says so rather than offering a control that
 * cannot work.
 */
export const SIGNER_ORG_ID: string = import.meta.env.VITE_SIGNER_ORG_ID ?? "";

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════
 * THE CLIENT API KEY — what this app authenticates ITSELF with, as distinct from a user
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `/user/v1` is behind TWO credentials answering two questions: `Authorization: Bearer` says WHICH WALLET,
 * and `X-Client-Token` says WHICH APP. This key is what mints the second one, once, at
 * `POST /user/v1/client/auth`. See `api/http.ts` for the handshake.
 *
 * ⚠️ EMPTY IS A REAL AND CORRECT CONFIGURATION: it means "something in front of me already adds the header".
 * That is how this app runs in production — nginx injects a pre-minted token for `/user/v1/` and the bundle
 * has never held a key. Set this ONLY when the app is served from an origin that is not ours: a `vite dev`
 * server, a preview deploy, or a third-party front end. With it unset the client sends no token and behaves
 * exactly as it always has, so turning this on cannot change the deployed path.
 *
 * ⚠️ AND SAY THE UNCOMFORTABLE PART OUT LOUD: A KEY IN A BROWSER BUNDLE IS PUBLIC. Anyone can read it out of
 * the JS. It is not a secret and must never be treated as one — it identifies an app, it does not authorise
 * spending. The thing that protects money is the bearer, which is per-user, short-lived and never in this
 * file. If the client gate is ever expected to bound anything against a determined party, the key has to
 * move behind a server the way nginx does it, and this constant is the wrong mechanism for that.
 */
export const CLIENT_API_KEY: string = import.meta.env.VITE_CLIENT_API_KEY ?? "";

/**
 * The one socket.
 *
 * Derived from `location` rather than defaulted to a literal host, because the scheme MUST follow the page's:
 * a `ws://` socket opened from an `https://` page is blocked as mixed content, and the browser reports it as
 * a generic connection failure — indistinguishable from a server that is down, which is a debugging session
 * nobody should have to have twice.
 */
export const STREAM_URL: string =
  (import.meta.env.VITE_STREAM_URL as string | undefined) ??
  `${globalThis.location?.protocol === "https:" ? "wss" : "ws"}://${globalThis.location?.host ?? "localhost:5175"}/v1/stream`;
